import React, {useCallback, useMemo, useState} from "react";
import {TouchableOpacity, StyleSheet, Platform} from "react-native";

import {Icon, IconName} from "../../base/icon";
import {colors} from "../";

type IconButtonType = "primary" | "generative" | "destructive" | "transparent" | "tertiary";

type Size = "sm" | "md";

type Shape = "rounded" | "circle";

type GroupPosition = "leftEnd" | "middle" | "rightEnd";

type Props = {
  size?: Size;
  type?: IconButtonType;
  shape?: Shape;
  iconName: IconName;
  accessibilityLabel?: string;
  onPress: () => void;
  disabled?: boolean;
  // if this button is part of a group, which position is it in?
  groupPosition?: GroupPosition;
};

function IconButton({
  size = "sm",
  type = "primary",
  shape = "rounded",
  iconName,
  accessibilityLabel,
  onPress,
  disabled,
  groupPosition,
}: Props) {
  const [hovered, setHovered] = useState<boolean>(false);

  const iconColor = useMemo<string>(() => {
    if (disabled) {
      return type === "transparent" ? colors.gray[300] : colors.gray[200];
    }
    return type === "transparent"
      ? colors.blue[800]
      : type === "tertiary"
      ? colors.fg.neutral.primary.light
      : colors.fg.neutral.softer.light;
  }, [disabled, type]);

  const circle = useMemo(() => shape === "circle", [shape]);

  const onMouseEnter = useCallback(() => {
    setHovered(true);
  }, []);

  const onMouseLeave = useCallback(() => {
    setHovered(false);
  }, []);

  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{disabled}}
      disabled={disabled}
      style={[
        styles.container,
        styles[`${type}Container`],
        styles[`${size}Container`],
        circle && styles.circleContainer,
        hovered && styles[`${type}HoveredContainer`],
        disabled && styles[`disabled${type === "transparent" ? "Transparent" : ""}Container`],
        groupPosition && styles[groupPosition],
      ]}
      onPress={onPress}
      {...Platform.select({
        web: {
          onMouseEnter,
          onMouseLeave,
        },
      })}
    >
      <Icon size={size} name={iconName} color={iconColor} />
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  container: {
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 8,
  },
  circleContainer: {
    borderRadius: 99,
  },
  smContainer: {
    width: 32,
    height: 32,
  },
  mdContainer: {
    width: 40,
    height: 40,
  },
  primaryContainer: {
    backgroundColor: colors.blue[800],
  },
  generativeContainer: {
    backgroundColor: colors.green[500],
  },
  destructiveContainer: {
    backgroundColor: colors.error[700],
  },
  transparentContainer: {
    backgroundColor: "transparent",
  },
  primaryHoveredContainer: {
    backgroundColor: colors.blue[900],
  },
  generativeHoveredContainer: {
    backgroundColor: colors.green[600],
  },
  destructiveHoveredContainer: {
    backgroundColor: colors.error[800],
  },
  transparentHoveredContainer: {
    backgroundColor: "rgba(0, 0, 0, 0.04)",
  },
  disabledContainer: {
    backgroundColor: colors.gray[400],
  },
  disabledTransparentContainer: {
    backgroundColor: colors.gray[100],
  },
  tertiaryContainer: {
    backgroundColor: colors.bg.transparent.low.light,
  },
  tertiaryHoveredContainer: {
    backgroundColor: colors.gray[50],
  },
  leftEnd: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
  },
  middle: {
    borderRadius: 0,
  },
  rightEnd: {
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
  },
});

export default React.memo(IconButton);
